import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import alertify from 'alertifyjs';
import { Subscription } from 'rxjs';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { VendorDetailsModalComponent } from '../../../components/vendor-details-modal/vendor-details-modal.component';
import { FilterDrawerComponent } from '../../../components/filter-drawer/filter-drawer.component';
import {
  FilterDrawerFieldConfig,
  FilterDrawerValue,
} from '../../../components/filter-drawer/filter-drawer.models';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { VendorApiItem, VendorRow, VendorsResponse } from './vendor-landing.model';
import { DropdownMenu } from "../../../components/dropdown-menu/dropdown-menu.component";
import { AuthenticationService } from '../../../modules/services/authentication.service';
import { WorkflowNotificationService } from '../../../modules/services/workflow-notification.service';

type VendorApprovalStatusFilter = 'ALL' | string;
type VendorApplicationStatusFilter = 'ALL' | string;
type VendorTypeFilter = 'ALL' | string;

type VendorFilter = {
  vendorId: string;
  vendorName: string;
  asin: string;
  mdaId: string;
  categoryId: string;
  approvalStatus: VendorApprovalStatusFilter;
  vendorType: VendorTypeFilter;
  applicationStatus: VendorApplicationStatusFilter;
};

@Component({
  selector: 'app-vendor-landing',
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    VendorDetailsModalComponent,
    FilterDrawerComponent,
    DropdownMenu
],
  templateUrl: './vendor-landing.component.html',
  styleUrl: './vendor-landing.component.scss',
})
export class VendorLandingComponent implements OnInit, OnDestroy {
  readonly columns = [
    'Vendor ID',
    'Vendor Name',
    'ASIN',
    'MDA',
    'Category',
    'Vendor Type',
    'Application Status',
    'Approval Status',
    'Actions',
  ];

  readonly pageSize = 10;
  vendors: VendorRow[] = [];
  allVendors: VendorRow[] = [];
  isLoadingVendors = false;
  openMenuIndex: number | null = null;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  readonly emptyFilters: VendorFilter = {
    vendorId: '',
    vendorName: '',
    asin: '',
    mdaId: '',
    categoryId: '',
    approvalStatus: 'ALL',
    vendorType: 'ALL',
    applicationStatus: 'ALL',
  };

  vendorFilters: VendorFilter = { ...this.emptyFilters };
  vendorFilterFields: FilterDrawerFieldConfig[] = [];
  isFilterDrawerOpen = false;
  private readonly subscriptions = new Subscription();

  isDetailsModalOpen = false;
  selectedVendor: VendorRow | null = null;
  isLoadingVendorDetails = false;

  authenticationService: AuthenticationService = inject(AuthenticationService);

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly workflowNotificationService: WorkflowNotificationService,
  ) {}

  ngOnInit(): void {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);

    this.fetchVendors(1);
    this.workflowNotificationService.connect();
    this.subscriptions.add(
      this.workflowNotificationService.workflowNotifications$.subscribe((notification) => {
        if (!notification.type?.startsWith('workflow.')) return;
        this.fetchVendors(this.currentPage);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get activeFilterCount(): number {
    return Object.entries(this.vendorFilters).filter(([key, value]) => {
      if (key === 'approvalStatus' || key === 'vendorType' || key === 'applicationStatus') {
        return value !== 'ALL';
      }
      return String(value ?? '').trim().length > 0;
    }).length;
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  applyFilters(raw: FilterDrawerValue): void {
    this.vendorFilters = {
      ...this.emptyFilters,
      ...Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    } as VendorFilter;

    const rawApproval = (raw['approvalStatus'] ?? 'ALL') as string;
    this.vendorFilters.approvalStatus = rawApproval || 'ALL';

    const rawVendorType = (raw['vendorType'] ?? 'ALL') as string;
    this.vendorFilters.vendorType = rawVendorType || 'ALL';

    const rawApplicationStatus = (raw['applicationStatus'] ?? 'ALL') as string;
    this.vendorFilters.applicationStatus = rawApplicationStatus || 'ALL';

    this.isFilterDrawerOpen = false;
    this.fetchVendors(1);
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  openAddVendorWizard(): void {
    void this.router.navigate(['/admin/vendors/new']);
  }

  openEditVendorWizard(vendor: VendorRow): void {
    this.openMenuIndex = null;
    void this.router.navigate(['/admin/vendors', vendor.id, 'edit']);
  }

  onViewDetails(vendor: VendorRow): void {
    this.openMenuIndex = null;
    this.isDetailsModalOpen = true;
    this.selectedVendor = null;
    this.isLoadingVendorDetails = true;

    this.apiService.getVendorById(vendor.id).subscribe({
      next: (response: any) => {
        const item = response?.data ?? response;
        this.selectedVendor = this.mapVendorDetailsRow(item);
        this.isLoadingVendorDetails = false;
      },
      error: () => {
        this.isLoadingVendorDetails = false;
        alertify.error('Unable to fetch vendor details. Please try again.');
      },
    });
  }

  closeDetailsModal(): void {
    this.isDetailsModalOpen = false;
    this.selectedVendor = null;
    this.isLoadingVendorDetails = false;
  }

  onDeactivate(vendor: VendorRow): void {
    this.openMenuIndex = null;
    this.updateVendorStatus(vendor, 'INACTIVE');
    alertify.success(`Vendor deactivated (${vendor.vendorReference}).`);
  }

  onBlacklist(vendor: VendorRow): void {
    this.openMenuIndex = null;
    this.updateVendorStatus(vendor, 'BLACKLISTED');
    alertify.success(`Vendor blacklisted (${vendor.vendorReference}).`);
  }

  exportCurrentTableAsCsv(): void {
    const headers = [
      'vendor_reference',
      'name',
      'mda_name',
      'category',
      'status',
      'approval_status',
      'asin',
      'rc_number',
      'phone_no',
      'address',
      'created_at',
    ];

    const rows = this.vendors.map((vendor) => ({
      vendor_reference: vendor.vendorReference,
      name: vendor.name,
      mda_name: vendor.mdaName,
      category: vendor.categoryName,
      status: vendor.status,
      approval_status: vendor.approvalStatus,
      asin: vendor.asin,
      rc_number: vendor.rcNumber,
      phone_no: vendor.phoneNo,
      address: vendor.address,
      created_at: vendor.createdAt,
    }));

    const escapeCsv = (value: unknown) => {
      const raw = String(value ?? '');
      if (raw.includes('"') || raw.includes(',') || raw.includes('\n')) {
        return `"${raw.replace(/\"/g, '""')}"`;
      }
      return raw;
    };

    const csv = [
      headers.join(','),
      ...rows.map((row) => headers.map((key) => escapeCsv((row as any)[key])).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vendors_page_${this.currentPage}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  getStatusPillClass(status: string): string {
    const normalized = String(status ?? '').toUpperCase();

    if (normalized === 'APPROVED') {
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    }

    if (normalized === 'ACTIVE') {
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    }

    if (normalized === 'PENDING') {
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    }

    if (normalized === 'QUERIED') {
      return 'bg-red-50 text-red-700 border border-red-200';
    }

    if (normalized === 'BLACKLISTED') {
      return 'bg-red-50 text-red-700 border border-red-200';
    }

    return 'bg-slate-50 text-slate-600 border border-slate-200';
  }

  goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoadingVendors) {
      return;
    }

    this.fetchVendors(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (!this.hasNextPage || this.isLoadingVendors) {
      return;
    }

    this.fetchVendors(this.currentPage + 1);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openMenuIndex = null;
    this.closeDetailsModal();
    this.closeFilters();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.vendor-actions')) return;
    this.openMenuIndex = null;
  }

  private buildVendorFilterFields(): void {
    const mdaOptions = Array.from(
      new Map(
        this.allVendors.map((vendor) => [
          vendor.mdaId,
          { label: vendor.mdaName || vendor.mdaId, value: vendor.mdaId },
        ]),
      ).values(),
    );

    const categoryOptions = Array.from(
      new Map(
        this.allVendors.map((vendor) => [
          vendor.categoryId,
          { label: vendor.categoryName || vendor.categoryId, value: vendor.categoryId },
        ]),
      ).values(),
    );

    const approvalOptions = Array.from(
      new Set(
        this.allVendors
          .map((v) => String(v.approvalStatus ?? '').trim())
          .filter((v) => v.length > 0),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const knownVendorTypes = ['CONTRACTOR', 'CONSULTANT'];
    const vendorTypeOptions = Array.from(
      new Set(
        [...knownVendorTypes, ...this.allVendors.map((v) => String(v.vendorType ?? '').trim())].filter(
          (v) => v.length > 0,
        ),
      ),
    ).sort((a, b) => a.localeCompare(b));

    const knownApplicationStatuses = ['APPROVED', 'PENDING', 'QUERIED'];
    const applicationStatusOptions = Array.from(
      new Set(
        [...knownApplicationStatuses, ...this.allVendors.map((v) => String(v.applicationStatus ?? '').trim())].filter(
          (v) => v.length > 0,
        ),
      ),
    ).sort((a, b) => a.localeCompare(b));

    this.vendorFilterFields = [
      {
        key: 'vendorId',
        label: 'Vendor ID',
        type: 'text',
        placeholder: 'Filter by vendor ID',
      },
      {
        key: 'vendorName',
        label: 'Vendor Name',
        type: 'text',
        placeholder: 'Filter by vendor name',
      },
      {
        key: 'asin',
        label: 'ASIN',
        type: 'text',
        placeholder: 'Filter by ASIN',
      },
      {
        key: 'mdaId',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: mdaOptions,
      },
      {
        key: 'categoryId',
        label: 'Category',
        type: 'searchable-select',
        placeholder: 'Select category',
        searchablePlaceholder: 'Search category options',
        options: categoryOptions,
      },
      {
        key: 'vendorType',
        label: 'Vendor Type',
        type: 'select',
        placeholder: 'Select vendor type',
        options: ['ALL', ...vendorTypeOptions].map((type) => ({
          label: type === 'ALL' ? 'All Vendor Types' : type,
          value: type,
        })),
      },
      {
        key: 'applicationStatus',
        label: 'Application Status',
        type: 'select',
        placeholder: 'Select application status',
        options: ['ALL', ...applicationStatusOptions].map((status) => ({
          label: status === 'ALL' ? 'All Application Status' : status,
          value: status,
        })),
      },
      {
        key: 'approvalStatus',
        label: 'Approval Status',
        type: 'select',
        placeholder: 'Select approval status',
        options: ['ALL', ...approvalOptions].map((status) => ({
          label: status === 'ALL' ? 'All Approval Status' : status,
          value: status,
        })),
      },
    ];
  }

  private updateVendorStatus(vendor: VendorRow, nextStatus: string): void {
    this.allVendors = this.allVendors.map((row) =>
      row.vendorReference === vendor.vendorReference
        ? { ...row, status: nextStatus }
        : row,
    );

    if (this.selectedVendor?.vendorReference === vendor.vendorReference) {
      this.selectedVendor = { ...this.selectedVendor, status: nextStatus };
    }

    this.applyClientFilters();
  }

  private fetchVendors(page = 1): void {
    this.isLoadingVendors = true;
    this.openMenuIndex = null;
    const offset = (page - 1) * this.pageSize;

    let params = new HttpParams().set('offset', offset).set('limit', this.pageSize);
    const vendorId = this.vendorFilters.vendorId.trim();
    const vendorName = this.vendorFilters.vendorName.trim();
    const asin = this.vendorFilters.asin.trim();
    const mdaId = this.vendorFilters.mdaId.trim();
    const categoryId = this.vendorFilters.categoryId.trim();
    const approvalStatus = String(this.vendorFilters.approvalStatus ?? '').trim();
    const vendorType = String(this.vendorFilters.vendorType ?? '').trim();
    const applicationStatus = String(this.vendorFilters.applicationStatus ?? '').trim();

    if (vendorId) params = params.set('vendor_reference', vendorId);
    if (vendorName) params = params.set('name', vendorName);
    if (asin) params = params.set('asin', asin);
    if (mdaId) params = params.set('mda_id', mdaId);
    if (categoryId) params = params.set('category_id', categoryId);
    if (vendorType && vendorType !== 'ALL') params = params.set('vendor_type', vendorType);
    if (applicationStatus && applicationStatus !== 'ALL') params = params.set('application_status', applicationStatus);
    if (approvalStatus && approvalStatus !== 'ALL') params = params.set('approval_status', approvalStatus);

    this.apiService.get('/vendors', { params }).subscribe({
      next: (response: VendorsResponse) => {
        this.allVendors = (response.data ?? []).map((item) => this.mapVendorRow(item));
        this.vendors = [...this.allVendors];
        this.buildVendorFilterFields();

        this.totalItems = response.pagination?.totalItems ?? this.vendors.length;
        this.totalPages = response.pagination?.totalPages ?? 1;
        this.currentPage = response.pagination?.currentPage ?? page;
        this.hasNextPage = response.pagination?.hasNextPage ?? false;
        this.hasPreviousPage = response.pagination?.hasPreviousPage ?? false;
        this.isLoadingVendors = false;
      },
      error: (error) => {
        this.allVendors = [];
        this.vendors = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.currentPage = 1;
        this.hasPreviousPage = false;
        this.hasNextPage = false;
        this.isLoadingVendors = false;
        const message =
          typeof error === 'string'
            ? error
            : 'Unable to fetch vendors. Please try again.';
        alertify.error(message);
      },
    });
  }

  private applyClientFilters(): void {
    // Backend search/filtering is used. Keep this method as a no-op fallback.
    this.vendors = [...this.allVendors];
  }

  private mapVendorRow(item: VendorApiItem): VendorRow {
    const tinRaw = String((item as any).tin ?? (item as any).tin_number ?? '').trim();
    const asin = String(item.asin ?? '').trim();
    return {
      id: String(item.id ?? ''),
      name: String(item.name ?? 'N/A'),
      email: String((item as any).email ?? ''),
      vendorReference: String(item.vendor_reference ?? item.id ?? ''),
      mdaId: String(item.mda_id ?? ''),
      mdaName: String(item.mda_name ?? item.mda_id ?? ''),
      categoryId: String(item.category_id ?? ''),
      categoryName: String(item.category_name ?? item.category_id ?? ''),
      status: String(item.status ?? 'PENDING'),
      approvalStatus: String(item.approval_status ?? ''),
      paymentStatus: String(item.payment_status ?? ''),
      asin,
      phoneNo: String(item.phone_no ?? ''),
      address: String(item.address ?? ''),
      businessClassification: String(item.business_classification ?? ''),
      natureOfService: String(item.nature_of_service ?? ''),
      rcNumber: String(item.rc_number ?? ''),
      dateOfIncorporation: String(item.date_of_incorporation ?? ''),
      tin: tinRaw || asin,
      tccNumber: String(item.tcc_number ?? ''),
      vendorType: String(item.vendor_type ?? ''),
      applicationStatus: String(item.application_status ?? ''),
      createdAt: String(item.created_at ?? ''),
    };
  }

  private mapVendorDetailsRow(item: any): VendorRow {
    const base = this.mapVendorRow(item as VendorApiItem);

    const directors = Array.isArray(item?.directors)
      ? item.directors.map((d: any) => ({
          asin: String(d?.asin ?? ''),
          name: String(d?.name ?? ''),
          phone: String(d?.phone_number ?? d?.phone ?? ''),
          email: String(d?.email ?? ''),
          address: String(d?.address ?? ''),
          bvn: String(d?.bvn ?? ''),
        }))
      : undefined;

    const bankDetails = item?.account
      ? {
          accountNumber: String(item.account?.account_number ?? ''),
          accountName: String(item.account?.account_name ?? ''),
          bankName: String(item.account?.bank_name ?? ''),
        }
      : item?.bank_details
        ? {
            accountNumber: String(item.bank_details?.account_number ?? ''),
            accountName: String(item.bank_details?.account_name ?? ''),
            bankName: String(item.bank_details?.bank ?? ''),
          }
        : undefined;

    const documents = Array.isArray(item?.documents)
      ? item.documents.map((doc: any) => ({
          name: String(doc?.document_name ?? doc?.name ?? ''),
          uploadedAt: String(doc?.updated_at ?? doc?.created_at ?? ''),
          uploaded_document_url: String(doc?.uploaded_document_url ?? doc?.uploadedDocumentUrl ?? ''),
        }))
      : undefined;

    return {
      ...base,
      directors,
      bankDetails,
      documents,
    };
  }
}
